import React, { useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { HelmetProvider } from "react-helmet-async";
import LoginPage from "./pages/user/LoginPage";
import ProfilePage from "./pages/user/Profile";
import UserProfile from "./pages/user/UserProfile";
import EventDetailsPage from "./pages/events/EventDetails";
import LeagueDetails from "./pages/league/LeagueDetails";
import CreateLeague from "./pages/league/CreateLeague";
import AcceptInvite from "./pages/league/AcceptInvite";
import Home from "./pages/Home";
import StyleGuide from "./pages/style-guide/StyleGuide";
import ToastTestPage from "./pages/style-guide/ToastTestPage";
import LandingPage from "./pages/landing-page";
import Loading from "./components/common/Loading";
import ScrollToTop from "./components/common/ScrollToTop";
import { LeagueProvider } from "./context/LeagueContext";
import { NotificationProvider } from "./context/NotificationContext";
import Help from "./pages/help/Help";
import LeagueHelp from "./pages/help/LeagueHelp";
import EventsHelp from "./pages/help/EventsHelp";
import DayShiftSportsMMAHelp from "./pages/help/DayShiftSportsMMAHelp";
import LineupHelp from "./pages/help/LineupHelp";
import ScoringHelp from "./pages/help/ScoringHelp";
import GlobalCompetition from "./pages/global/GlobalCompetition";
import MainLayout from "./layouts/MainLayout";
import LeaguesPage from "./pages/leagues/LeaguesPage";
import ScorecardPage from "./pages/leagues/ScorecardPage";
import EventsPage from "./pages/events/EventsPage";
import Admin from "./pages/user/Admin";
import SystemHealthPage from "./pages/admin/SystemHealthPage";
import FightCard from "./components/events/FightCard";
import ThreadViewPage from "./pages/threads/ThreadViewPage";
import SearchPage from "./pages/search/SearchPage";
import RulesPage from "./pages/rules/RulesPage";

import NotificationsPage from "./pages/notifications/NotificationsPage";
import NotificationPreferencesPage from "./pages/notifications/NotificationPreferencesPage";
import RequireAdmin from "./components/auth/RequireAdmin";
import ComingSoonGate from "./components/auth/ComingSoonGate";
import { useSharedEventBridgeNotifications } from "./hooks/notifications/useSharedEventBridgeNotifications";
import { useGlobalThreadNotifications } from "./hooks/notifications/useGlobalThreadNotifications";

const App = () => {
  const { isLoading } = useAuth0();

  if (isLoading) return <Loading />;

  return (
    <HelmetProvider>
      <NotificationProvider>
        <LeagueProvider>
          <ScrollToTop />
          <ComingSoonGate>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/" element={<PublicRoute />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Protected routes with MainLayout */}
              <Route element={<ProtectedLayout />}>
                <Route path="/home" element={<Home />} />
                {/* Admin routes - requires admin role */}
                <Route element={<RequireAdmin />}>
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/health" element={<SystemHealthPage />} />
                  <Route
                    path="/admin/events/:eventId/edit"
                    element={<FightCard />}
                  />
                </Route>
                <Route path="/style-guide" element={<StyleGuide />} />
                <Route path="/toast-test" element={<ToastTestPage />} />

                {/* Main navigational pages */}
                <Route path="/leagues" element={<LeaguesPage />} />
                <Route path="/scorecard" element={<ScorecardPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route
                  path="/globalcompetition"
                  element={<GlobalCompetition />}
                />

                {/* Legacy routes - kept for backward compatibility */}
                <Route
                  path="/league/events"
                  element={<Navigate to="/globalcompetition" replace />}
                />
                <Route path="/league/details" element={<LeagueDetails />} />
                <Route path="/league/:leagueId" element={<LeagueDetails />} />

                {/* League specific routes */}
                <Route path="/create-league" element={<CreateLeague />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/users/:userId" element={<UserProfile />} />
                <Route path="/search" element={<SearchPage />} />
                <Route
                  path="/leaderboard"
                  element={<Navigate to="/globalcompetition" replace />}
                />

                {/* Event specific routes */}
                <Route path="/events/:eventId" element={<EventDetailsPage />} />

                {/* Thread routes */}
                <Route path="/threads/:threadId" element={<ThreadViewPage />} />

                {/* Notification routes */}
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/notifications/preferences"
                  element={<NotificationPreferencesPage />}
                />

                <Route path="/rules" element={<RulesPage />} />
                <Route path="/help" element={<Help />} />
                <Route path="/help/league" element={<LeagueHelp />} />
                <Route path="/help/events" element={<EventsHelp />} />
                <Route
                  path="/help/dayshift-sports-mma"
                  element={<DayShiftSportsMMAHelp />}
                />
                <Route path="/help/lineup" element={<LineupHelp />} />
                <Route path="/help/scoring" element={<ScoringHelp />} />
              </Route>

              {/* Fallback route - redirect to landing page if not authenticated, home if authenticated */}
              <Route path="*" element={<AuthAwareRedirect />} />
            </Routes>
          </ComingSoonGate>
        </LeagueProvider>
      </NotificationProvider>
    </HelmetProvider>
  );
};

const ProtectedLayout = () => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const [probing, setProbing] = React.useState(true);

  // Initialize EventBridge WebSocket notifications for authenticated users
  const { isConnected, connectionError } = useSharedEventBridgeNotifications();

  // Initialize global thread notification handling
  const { isGlobalHandlerActive } = useGlobalThreadNotifications();

  // Debug logging for notification connection
  useEffect(() => {
    if (isAuthenticated) {
      console.log("🔔 Notification system status:", {
        isConnected,
        connectionError,
        globalThreadHandler: isGlobalHandlerActive,
      });
    }
  }, [isAuthenticated, isConnected, connectionError, isGlobalHandlerActive]);

  useEffect(() => {
    let mounted = true;
    const probe = async () => {
      if (!isAuthenticated) {
        if (mounted) setProbing(false);
        return;
      }
      try {
        await getAccessTokenSilently();
      } catch (err) {
        const msg = (err?.error || err?.message || "").toLowerCase();
        if (
          msg.includes("login_required") ||
          msg.includes("consent_required") ||
          msg.includes("missing_refresh_token") ||
          msg.includes("missing refresh token") ||
          msg.includes("invalid_grant")
        ) {
          await loginWithRedirect();
          return;
        }
      } finally {
        if (mounted) setProbing(false);
      }
    };
    probe();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  if (isLoading || probing) return <Loading />;

  return isAuthenticated ? (
    <MainLayout>
      <Outlet />
    </MainLayout>
  ) : (
    <Navigate to="/" replace />
  );
};

// Handles redirection for the login page to prevent logged-in users from seeing it
const LoginRoute = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  return isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />;
};

// Public route for landing page that redirects to home if authenticated
const PublicRoute = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  return isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />;
};

// Redirects based on authentication status
const AuthAwareRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  return isAuthenticated ? (
    <Navigate to="/home" replace />
  ) : (
    <Navigate to="/" replace />
  );
};

export default App;
